import { useRef } from "react";
import React from "react";
import { Plan } from "../../types/types";
import { useTranslation } from "react-i18next";

export enum PlanAction {
  CURRENT = "current",
  DOWNGRADE = "downgrade",
  UPGRADE = "upgrade",
  GET = "get",
}

interface TransferPlanItemProps {
  plan: Plan;
  isSelected: boolean;
  onSelect: (plan: Plan) => void;
  action: PlanAction;
  isLastItem?: boolean;
}

/**
 * Small presentational component for a single plan row.
 * Keeps the AsaTransferLanding map section clean.
 */
const TransferPlanItem: React.FC<TransferPlanItemProps> = ({ plan, isSelected, onSelect, action, isLastItem }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const circleCheck = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="12" fill="#705AE2" />
      <path d="M17 8L10.5 14.5L7 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const getItemCommand = (): string => {
    switch (action) {
      case PlanAction.CURRENT:
        return t("currentPlan");
      case PlanAction.DOWNGRADE:
        return t("downgrade");
      case PlanAction.UPGRADE:
        return t("upgrade");
      case PlanAction.GET:
      default:
        return t("get");
    }
  };

  const getButtonStyle = (): string => {
    switch (action) {
      case PlanAction.CURRENT:
        return "w-[100px] bg-[#4E4E4E] cursor-not-allowed";
      case PlanAction.DOWNGRADE:
        return "w-[80px] bg-[#705AE2]";
      case PlanAction.UPGRADE:
        return "w-[80px]  bg-[#705AE2]";
      case PlanAction.GET:
      default:
        return "w-[60px] bg-[#705AE2] hover:bg-[#6047d1]";
    }
  };
  
  const itemcommand = getItemCommand();
  const isButtonDisabled = action === PlanAction.CURRENT;

  const moveBox = (box: HTMLDivElement) => {
    if (!box) return;
    box.style.transformOrigin = "50% 50%";
    box.animate(
      [
        { transform: "translateX(-24px) rotateX(-180deg)" },
        { transform: "translateX(6px) rotateX(90deg)" },
        { transform: "translateX(0px) rotateX(0deg)" },
      ],
      {
        duration: 700,
        iterations: 1,
        easing: "cubic-bezier(.2,.9,.3,1)",
        fill: "forwards",
      }
    );
  };

  const handleSelect = (plan: Plan) => {
    if (isSelected || isButtonDisabled) return;
    moveBox(rowRef.current!);
    onSelect(plan);
  };

  const borderClass = isLastItem ? "border-b-0" : "border-b-2";

  return (
    <div
      ref={rowRef}
      className={`${borderClass} dark:border-white-500/50 border-gray-500/50 w-full flex-shrink-0 pt-2 pb-2 flex flex-row justify-center items-center`}
    >
      {isSelected && <div className="mr-2 flex-shrink-0">{circleCheck()}</div>}

      <div className="px-2 truncate w-32 dark:text-[#D8D8D8] text-[#4E4E4E] font-poppins text-sm font-medium leading-5 tracking-[-0.1px]">
        {plan?.subscriptionTitle}
      </div>

      <div className="dark:text-[#D8D8D8] text-[#4E4E4E] font-poppins text-sm font-medium leading-5 tracking-[-0.1px]">
        - ${plan?.oneTimeAmount}
      </div>

      <div
        className={
          "dark:shadow-[0_4px_6px_rgba(10,_10,_10,_0.7)] shadow-[0_4px_6px_rgba(120,_120,_120,_0.7)] ml-auto rounded-[30px]  h-[24px] flex-shrink-0 transition-all " +
          (isSelected ? "opacity-0" : "opacity-100") +
          " " +
          getButtonStyle()
        }
      >
        <button
          type="button"
          onClick={() => handleSelect(plan)}
          disabled={isButtonDisabled}
          className="w-full h-full text-white text-center font-poppins text-[10px] font-bold leading-5 tracking-[-0.1px] uppercase disabled:cursor-not-allowed"
        >
          {itemcommand}
        </button>
      </div>
    </div>
  );
};

export default TransferPlanItem;